import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Spin } from "antd";
import {
  mapboxToken,
  mapboxOwner,
  terrainId,
  satelliteId,
} from "../../../configuration";
import { addDesignedButtons } from "../../Shipments/CreateShipments/map-utils";
import { fetchLocationAlertsRequest } from "../../../store/actions/asset";
import getAddressfromLatLong from "../../../global/utility/getAddressfromLatLong";
import {
  capitalize,
  returnPopups,
  popUpAndIndicator,
  addAccuracyCircle,
} from "../../../global/utility/Utils";
import {
  fetchLocationAlertsAction,
  fetchProfileRequest,
  fetchAlertsContentRequest,
} from "../../../store/actions/shipment";
import { updateUI } from "../../../store/actions/asset";
import dayjs from "dayjs";
import { fetchRequest as fetchDeviceProfile } from "../../../store/actions/deviceProfile";
import capitalizeFirstLetter from "../../../global/utility/capitalizeFirstLetter";
import { updateFilters } from "../../../store/actions/asset";
import { add } from "vega-lite/build/src/compositemark";

function AssetMap({ height, hasPopup = false }) {
  const allAssetsListData = useSelector(
    (state) => state.asset.data.allAssetsListData
  );
  const assetMapLocationAlertsLoader = useSelector(
    (state) => state.asset.isLoading.assetMapLocationAlertsLoader
  );
  const isLoadingAssetMap = useSelector(
    (state) => state.asset.isLoading.assetMap
  );
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const dispatch = useDispatch();
  const pathPoints = useSelector((state) => state.asset.assetTrackData);
  const [originCoord, setOriginCoord] = useState(null);
  const [pointsNo, setPointsNo] = useState(0);
  const dynamicMetricValues = useSelector((state) => state.asset.ui.location);
  const prevRef = useRef([0, 0]);
  
  useEffect(() => setPointsNo(pathPoints.length - 1), [pathPoints]);

  const validateCoord = (coord) => {
    if (coord && Array.isArray(coord)) {
      if (!isNaN(coord[0]) && !isNaN(coord[1])) {
        if (
          coord[0] >= -180 &&
          coord[0] <= 180 &&
          coord[1] >= -90 &&
          coord[1] <= 90
        ) {
          return true;
        }
        return false;
      }
      return false;
    } else {
      return false;
    }
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const currentIndxRef = useRef(0);

  useEffect(() => {
    if (assetMapLocationAlertsLoader) {
      return;
    }

    // Find the index with the latest timestamp
    let latestIndex = 0;
    if (pathPoints.length > 0) {
      latestIndex = pathPoints.reduce((latestIdx, current, currentIdx) => {
        if (!pathPoints[latestIdx].timestamp) return currentIdx;
        if (!current.timestamp) return latestIdx;
        return new Date(current.timestamp) > new Date(pathPoints[latestIdx].timestamp)
          ? currentIdx
          : latestIdx;
      }, 0);
    }

    handleUpdate(latestIndex);
    // return () => dispatch(updateUI({
    //   location: {
    //     temperature: "",
    //     battery: "",
    //     datatimestamp: "",
    //     currentIndex: 0,
    //   }
    // }));
  }, [assetMapLocationAlertsLoader]);

  useEffect(() => {
    currentIndxRef.current = currentIndex;
  }, [currentIndex]);

  async function executeIfChanged(getAddressfromLatLong, value, prevRef) {
    if (
      Array.isArray(prevRef.current) &&
      Array.isArray(value) &&
      prevRef.current[0] !== value[0] &&
      prevRef.current[1] !== value[1]
    ) {
      if (!validateCoord(value)) return;
      const address = await getAddressfromLatLong(value[1], value[0]);
      prevRef.current = Array.isArray(value) ? [...value] : value;
      return address;
    } else {
      return null;
    }
  }

  const handleUpdate = async (currentIndex) => {
    const point = pathPoints[currentIndex];
   
    const address = await executeIfChanged(
      getAddressfromLatLong,
      [point?.longitude, point?.latitude],
      prevRef
    );

    dispatch(
      updateUI({
        location: {
          currentIndex: currentIndex,
          datatimestamp: point?.timestamp,
          temperature: point?.temperature,
          battery: point?.battery,
          location: [point?.longitude, point?.latitude],
          address: address,
        },
      })
    );
  };

  useEffect(() => {
    if (isLoadingAssetMap) return;
    if (!mapRef.current) return;
    handleUpdate(currentIndex);
    const clustersSource = mapRef.current.getSource("clusters");
    
    const updatedAsset = {
      ...allAssetsListData?.[0],
      location: {
        lat_value: pathPoints?.[currentIndex]?.latitude,
        long_value: pathPoints?.[currentIndex]?.longitude,
      },
    };
    if (clustersSource) {
      clustersSource.setData(createGeoJsonSource([updatedAsset]));
    }
  }, [currentIndex, isLoadingAssetMap]);

  // Play/pause logic
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          if (prevIndex < pointsNo) {
            return prevIndex + 1;
          } else {
            clearInterval(intervalRef.current);
            setIsPlaying(false);
            return prevIndex;
          }
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, pointsNo]);

  // Playback controls
  const handleStartClick = (play) => {
    if (!pathPoints || pathPoints.length === 0) return;
    if (play) {
      if (currentIndex === pointsNo) setCurrentIndex(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };
  const handleRangeChange = (e) => {
    setCurrentIndex(parseInt(e.target.value));
  };
  const handleForwardClick = () => {
    if (currentIndex < pointsNo) setCurrentIndex(currentIndex + 1);
  };
  const handleBackwardClick = () => {
    if (currentIndex > 1) setCurrentIndex(currentIndex - 1);
  };

  const adjustOverlappedCoord = (seen, coord, magnitude = 0.0001) => {
    const coordStr = coord.join(",");
    if (seen.includes(coordStr)) {
      const adjustedCoord = [
        coord[0] + (Math.random() - 0.5) * magnitude,
        coord[1] + (Math.random() - 0.5) * magnitude,
      ];
      seen.push(adjustedCoord.join(","));
      return adjustedCoord;
    } else {
      seen.push(coordStr);
      return coord;
    }
  };

  const createGeoJsonSource = (assetsData) => {
    const seen = [];
    const bounds = new mapboxgl.LngLatBounds();
    const features = assetsData
      ?.map((asset) => {
        let coord = asset?.currentlocation
          ? [
              asset.currentlocation?.long_value,
              asset.currentlocation?.lat_value,
            ]
          : [asset.location?.long_value, asset.location?.lat_value] || [];

        if (currentIndex > 0 && pathPoints?.length > 0) {
          coord = [asset?.location?.long_value, asset?.location?.lat_value];
        }

        if (!validateCoord(coord)) {
          return;
        }
        bounds.extend(coord);
        const locationname = [];
        if (asset?.currentlocation) {
          if (asset?.currentlocation?.name) {
            locationname.push(
              capitalizeFirstLetter(asset?.currentlocation?.name)
            );
          }
          if (asset?.currentlocation?.floor?.name) {
            locationname.push(
              capitalizeFirstLetter(asset?.currentlocation?.floor?.name)
            );
          }
          if (asset?.currentlocation?.floor?.zone?.zone_name) {
            locationname.push(
              capitalizeFirstLetter(
                asset?.currentlocation?.floor?.zone?.zone_name
              )
            );
          }
        } else if (asset?.location) {
          if (asset?.location?.name) {
            locationname.push(capitalizeFirstLetter(asset?.location?.name));
          }
          if (asset?.location?.floor?.name) {
            locationname.push(
              capitalizeFirstLetter(asset?.location?.floor?.name)
            );
          }
          if (asset?.location?.floor?.zone?.zone_name) {
            locationname.push(
              capitalizeFirstLetter(asset?.location?.floor?.zone?.zone_name)
            );
          }
        }
       
        const location = locationname.length
          ? locationname.join("-")
          : asset?.location?.address || "Not assigned";

        return {
          type: "Feature",
          properties: {
            id: 1,
            title: asset.assetname,
            location,
            value: asset,
            status: asset.assetstatus,
          },
          geometry: {
            type: "Point",
            coordinates: adjustOverlappedCoord(seen, coord),
          },
        };
      })
      .filter(Boolean);

    const source = {
      type: "FeatureCollection",
      features,
    };

    const sw = [bounds?._sw?.lng, bounds?._sw?.lat];
    const ne = [bounds?._ne?.lng, bounds?._ne?.lat];
    const duration = hasPopup ? 0 : 1000;
    if (validateCoord(sw) && validateCoord(ne)) {
      mapRef.current?.fitBounds(bounds, {
        padding: 40,
        maxZoom: 15,
        duration: duration,
      });
    }

    return source;
  };

  useEffect(() => {
    if (!mapContainer.current || mapRef.current || isLoadingAssetMap) return;
    const firstAsset = allAssetsListData[0];
    const centerCoordinates =
      firstAsset &&
      validateCoord([
        firstAsset.currentlocation?.long_value,
        firstAsset.currentlocation?.lat_value,
      ])
        ? [
            firstAsset.currentlocation.long_value,
            firstAsset.currentlocation.lat_value,
          ]
        : [-98.5795, 39.8283];

    if (validateCoord(centerCoordinates)) {
      setOriginCoord(centerCoordinates);
    }

    mapboxgl.accessToken = mapboxToken;
    if (!mapboxgl.supported()) {
      console.error("WebGL is not supported in this browser. Map cannot be initialized.");
      return;
    }
    if (!mapRef.current) {
      try {
        mapRef.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: `mapbox://styles/${mapboxOwner}/${terrainId}`,
          center: centerCoordinates,
          zoom: 9,
        });
      } catch (e) {
        console.error("Failed to initialize map:", e);
        return;
      }

      mapRef.current.addControl(
        new mapboxgl.NavigationControl({ visualizePitch: true }),
        "bottom-right"
      );
      mapRef.current.addControl(new mapboxgl.FullscreenControl(), "top-right");
      addDesignedButtons(mapRef.current, terrainId, satelliteId, mapboxOwner);
    }

    const addMapLayers = () => {
      if (!mapRef.current.getSource("clusters")) {
        mapRef.current.addSource("clusters", {
          type: "geojson",
          data: createGeoJsonSource(allAssetsListData),
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
          clusterProperties: {
            has_critical: [
              "max",
              ["case", ["==", ["get", "status"], "ERROR"], 1, 0],
            ],
            has_warning: [
              "max",
              ["case", ["==", ["get", "status"], "WARNING"], 1, 0],
            ],
            has_notification: [
              "max",
              ["case", ["==", ["get", "status"], "NOTIFICATION"], 1, 0],
            ],
            has_completed: [
              "max",
              ["case", ["==", ["get", "status"], "COMPLETED"], 1, 0],
            ],
            has_noAlert: [
              "max",
              ["case", ["==", ["get", "status"], "OK"], 1, 0],
            ],
          },
        });

        mapRef.current.addLayer({
          id: "clusters",
          type: "circle",
          source: "clusters",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": [
              "case",
              ["==", ["get", "has_critical"], 1],
              "#ff8027",
              ["==", ["get", "has_warning"], 1],
              "#fed60e",
              ["==", ["get", "has_notification"], 1],
              "#006FC1",
              ["==", ["get", "has_completed"], 1],
              "#AAD8F9",
              "#00B845",
            ],
            "circle-radius": 20,
          },
        });

        mapRef.current.addLayer({
          id: "cluster-count",
          type: "symbol",
          source: "clusters",
          filter: ["has", "point_count"],
          layout: {
            "text-field": "{point_count_abbreviated}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
            "text-offset": [0, 0],
          },
        });

        mapRef.current.addLayer({
          id: "unclustered-point",
          type: "circle",
          source: "clusters",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": [
              "case",
              ["==", ["get", "status"], "ERROR"],
              "#ff8027",
              ["==", ["get", "status"], "WARNING"],
              "#fed60e",
              ["==", ["get", "status"], "NOTIFICATION"],
              "#006FC1",
              ["==", ["get", "status"], "COMPLETED"],
              "#AAD8F9",
              "#00B845",
            ],
            "circle-radius": 15,
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff",
          },
        });

        mapRef.current.addLayer({
          id: "unclustered-point-label",
          type: "symbol",
          source: "clusters",
          filter: ["!", ["has", "point_count"]],
          layout: {
            "text-field": "{id}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
            "text-offset": [0, 0],
          },
          paint: {
            "text-color": "#fff",
          },
        });

        const removePopups = () => {
          const popups = document.getElementsByClassName("mapboxgl-popup");
          while (popups.length > 0) {
            popups[0].remove();
          }
        };
        mapRef.current?.on("mouseenter", "unclustered-point", () => {
          mapRef.current.getCanvas().style.cursor = "pointer";
        });
        mapRef.current?.on("mouseenter", "clusters", () => {
          mapRef.current.getCanvas().style.cursor = "pointer";
        });
        mapRef.current?.on("mouseleave", "unclustered-point", () => {
          mapRef.current.getCanvas().style.cursor = "";
        });
        mapRef.current?.on("mouseleave", "clusters", () => {
          mapRef.current.getCanvas().style.cursor = "";
        });

        const clusterPopup = async (e) => {
          const clusterId = e?.features[0]?.properties?.cluster_id;
          const coordinates = e.features[0].geometry.coordinates.slice();
          const clusterSource = mapRef.current?.getSource("clusters");
          const popups = [];
          let popup = null;
          let accumulator = [];
          await new Promise((resolve) => {
            clusterSource.getClusterLeaves(
              clusterId,
              e?.features[0]?.properties?.point_count || 0,
              0,
              async (err, leaves) => {
                if (err) return;
                for (const feature of leaves) {
                  const { title, location, status, value } = feature.properties;
                  const popupStr = returnPopups({
                    status: status === "ERROR" ? "Critical" : status,
                    locationName: title,
                    Address: location,
                    statusClass:
                      status === "ERROR"
                        ? "critical"
                        : status === "OK"
                        ? "no-alert"
                        : status,
                    assetId: value?.assetid,
                    noNeed: false,
                  });
                  popups.push(`<div class="mappopup-info">${popupStr}</div>`);
                  accumulator.push(value?.assetid);
                }
                let popupHtml = popups.join(
                  `<div class="map-data-seperator"></div>`
                );
                popup = new mapboxgl.Popup({
                  closeButton: true,
                  closeOnClick: true,
                  className: "customPopupCurrMapClass",
                })
                  .setLngLat(coordinates)
                  .setHTML(
                    `<div class="cluster-map-popup-container" style="max-height:220px;overflow-y:auto;">
              ${popupHtml}
            </div>`
                  );
                popup.addTo(mapRef.current);
                mapRef.current.easeTo({
                  center: coordinates,
                  zoom: mapRef.current.getZoom(),
                  offset: [0, 110],
                  duration: 500,
                });
                resolve();
              }
            );
          });
          return { accumulator, popup };
        };

        mapRef.current.on("mouseover", "clusters", async (e) => {
          removePopups();
          const { accumulator, popup } = await clusterPopup(e);
          popup?.on("close", () => {});
        });
        mapRef.current?.on("zoom", () => {
          removePopups();
        });

        const unclusterPopup = async (e) => {
          const coordinates = e.features[0].geometry.coordinates.slice();
          const { title, id, status, location } = e.features[0].properties;
          const statusClass =
            status === "OK"
              ? "no-alert"
              : status === "ERROR"
              ? "critical"
              : status;
          const assetId = id || "Unknown";
          const address = location || "Unknown";
          const popupStr = returnPopups({
            status: status === "ERROR" ? "Critical" : status,
            locationName: title,
            Address: address,
            statusClass,
            noNeed: false,
            statusid: "statusClickNonCluster",
            assetId,
          });
          const popup = new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: true,
            className: "shipment-popup",
          })
            .setLngLat(coordinates)
            .setHTML(popupStr);

          popup.on("open", () => {
            document
              .getElementById("statusClickNonCluster")
              ?.addEventListener("click", (e) => {});
          });

          popup.addTo(mapRef.current);
          mapRef.current.easeTo({
            center: coordinates,
            zoom: mapRef.current.getZoom(),
            offset: [0, 110],
            duration: 500,
          });
          return popup;
        };

        mapRef.current.on("mouseover", "unclustered-point", async (e) => {
          removePopups();
          const popup = await unclusterPopup(e);
          popup?.on("close", () => {});
        });

        mapRef.current.on("click", "unclustered-point", async (e) => {
          const { value } = e.features[0].properties;
          const parsedvalue = JSON.parse(value);
         
          dispatch(
            updateFilters({
              filters: {
                assetid: {
                  sort: "asc",
                  option: Object.hasOwn(parsedvalue, "assetid")
                    ? [parsedvalue.assetid]
                    : [],
                },
              },
              sort: {
                field: "createdon",
                order: "desc",
              },
            })
          );
          const popup = await unclusterPopup(e);
          popup?.on("close", () => {});
        });
        mapRef.current?.on("click", "clusters", async (e) => {
          removePopups();
          const { accumulator, popup } = await clusterPopup(e);
     
          dispatch(
            updateFilters({
              filters: {
                assetid: {
                  sort: "asc",
                  option: Array.isArray(accumulator) ? accumulator : [],
                },
              },
              sort: {
                field: "createdon",
                order: "desc",
              },
            })
          );
          popup?.on("close", () => {});
        });
      } else {
        mapRef.current
          .getSource("clusters")
          .setData(createGeoJsonSource(allAssetsListData));
      }
    };

    mapRef.current.on("styledata", addMapLayers);
    //mapRef.current.on("load", addMapLayers);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [allAssetsListData, isLoadingAssetMap]);

  return (
    <div className="ats-tab-card ats-mapbox-container">
      <Spin
        //style={{ backgroundColor: "white" }}
        spinning={isLoadingAssetMap || assetMapLocationAlertsLoader}
        tip="Loading..."
      >
        <div
          ref={mapContainer}
          style={{ width: "100%", height: height || "35dvh" }}
        ></div>
        {hasPopup && (
          <div className="popup-container assetMapPopup">
            <div className="popup-controls">
              <button className="btn" onClick={handleBackwardClick}>
                <i className="bi bi-skip-start-circle large-icon-play"></i>
              </button>
              <button
                className="btn"
                onClick={() => {
                  if (pathPoints.length === 0) return;
                  setIsPlaying(!isPlaying);
                  handleStartClick(!isPlaying);
                }}
              >
                <i
                  className={`bi ${
                    !isPlaying ? "bi-play-circle" : "bi-stop-circle"
                  } large-icon-play`}
                ></i>
              </button>
              <button className="btn" onClick={handleForwardClick}>
                <i className="bi bi-skip-end-circle large-icon-play"></i>
              </button>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max={pointsNo}
                  value={currentIndex}
                  onChange={handleRangeChange}
                  className="slider"
                />
                <span className="timestamp">
                  {currentIndex === 0
                    ? pathPoints.length > 0
                      ? dayjs(pathPoints[0]?.timestamp).format(
                          "DD MMM YYYY HH:mm:ss"
                        )
                      : dayjs(allAssetsListData[0]?.updatedat).format(
                          "DD MMM YYYY HH:mm:ss"
                        )
                    : dayjs(dynamicMetricValues?.datatimestamp).format(
                        "DD MMM YYYY HH:mm:ss"
                      )}
                </span>
              </div>
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
}

AssetMap.propTypes = {
  height: PropTypes.string,
};

export default AssetMap;
